"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "@/i18n/routing";
import CookieModal from "./CookieModal"; // Preferences modal
import CookiePolicyPopup from "./CookiePolicyPopup"; // Optional short popup
import useCookieConsent from "../../hooks/useCookieConsent"; 
import { useTranslations } from "next-intl";

export default function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const {hasConsented, updateConsent} = useCookieConsent();
  const t = useTranslations("CookieComponents");

  useEffect(() => {
    if (!hasConsented) {
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    } else {
      setShowBanner(false);
    }
  }, [hasConsented]);
  
  const acceptAllFromBanner = () => {
    updateConsent("essential,functional,analytics,marketing");
    setShowBanner(false);
  };

  return (
    <>
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="lg:w-[600px] fixed bottom-0 left-0 right-0 z-50 p-4 pb-10"
          >
            <div className="max-w-xl w-full bg-white-80 backdrop-blur-xl rounded-xl shadow-xl p-5 border border-white/20 text-center md:text-left">
              <p className="text-sm text-gray-900 leading-relaxed">
                {t("CookieBanner.description")}{" "}                 
                <br />
                <br />
                {t("CookieBanner.learnMore")}{" "}
                <button
                  onClick={() => setShowPolicy(true)}
                  className="text-brand-secondary hover:underline"
                >
                  {t("links.cookiePolicy")}
                </button>{" "}
                {t("and")}{" "}
                <Link
                  href="/legal?doc=PrivacyPolicy"
                  className="text-brand-secondary hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("links.privacyPolicy")}
                </Link>
                .
              </p>
              <div className="flex flex-wrap justify-center md:justify-end gap-3 mt-5">
                <button
                  onClick={() => setShowPreferences(true)}
                  className="px-6 py-2 bg-brand-gradient text-sm text-white rounded-sm  transition"
                >
                 <span className="font-button font-[number:var(--button-font-weight)] [font-style:var(--button-font-style)]">{t("CookieBanner.manage")}</span>  
                </button>
                
                <button
                  onClick={acceptAllFromBanner}
                  className="px-6 py-2 bg-brand-gradient text-sm text-white rounded-sm transition"
                >
                  <span className="font-button font-[number:var(--button-font-weight)] [font-style:var(--button-font-style)]">
                    {t("CookieBanner.acceptAll")}
                  </span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      { (showPolicy || showPreferences) && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-md"
          role="dialog"
          aria-modal="true"
        >
          {showPolicy && (
            <CookiePolicyPopup
              key="policy"
              onClose={() => setShowPolicy(false)}
              onManagePreferences={() => {
                setShowPolicy(false);
                setShowPreferences(true);
              }}
            />
          )}

          {showPreferences && (
              <CookieModal key="preferences" onClose={() => setShowPreferences(false)} />
          )}      
        </div>
      )}

    </>
  );
}
